import React, { useEffect } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import translate from '../../core/translate'
import { ExternalAdvertisementHooks } from './ExternalAdvertisementHooks'
import Content from '../../blocks.app/content/content'
import { TabList } from '../../organisms/TabList'
import { Typography } from '../../atoms/Typography'
import styles from './externalAdvertisement.jcss'
import Catalog from '../../blocks.app/catalog/catalog'
import { DevicesSidebar } from './__devicesSidebar/DevicesSidebar'
import { isExternalAdvertisementTabId } from './externalAdvertisement.types'
import { IExternalAdvertisementDevice } from './externalAdvertisement.types'
import { ItemData } from '../../core/models/Template'
import { ContentModeration } from './__contentModeration/ContentModeration'
import { appActions } from '../../blocks.app/app/app.state'

export const ExternalAdvertisement: React.FC = () => {
    const dispatch = useDispatch()
    const isMobile = useSelector<RootStateOrAny, boolean>((state) => state.app.isMobile)
    const {
        selectedTab,
        setSelectedTab,
        tabList,
        selectedInfo,
        setSelectedInfo,
        selectedItems,
        setSelectedItems,
        setModerationCount,
    } = ExternalAdvertisementHooks()
    const hasDevicesSidebar = selectedTab === 'devices' || selectedTab === 'advertisementDevices'

    const handleCancelSelection = (): void => {
        setSelectedInfo(null)

        if (selectedTab === 'advertisementDevices') {
            setSelectedItems([])
        }
    }

    const renderDevicesSidebar = (): React.ReactElement => (
        <DevicesSidebar
            selected={selectedInfo}
            selectedTab={selectedTab}
            selectedItems={selectedItems}
            onCancel={handleCancelSelection}
        />
    )

    useEffect(() => {
        dispatch(appActions.toggleMobShowToolbarBtn(isMobile && hasDevicesSidebar))

        if (!isMobile || !hasDevicesSidebar) {
            dispatch(appActions.toggleMobToolbar(false))
        }

        return () => {
            dispatch(appActions.toggleMobToolbar(false))
            dispatch(appActions.toggleMobShowToolbarBtn(false))
        }
    }, [dispatch, hasDevicesSidebar, isMobile])

    const renderDevices = (): React.ReactElement => {
        return (
            <div className={styles.devicesPageWrapper}>
                <div className={styles.catalogWrapper}>
                    <Catalog
                        key={'displays__catalog'}
                        type={'displays'}
                        selectedInfo={selectedInfo}
                        onSelectInfo={setSelectedInfo}
                        onDoubleClickObject={() => {}}
                        onChange={() => {}}
                        useWindow={false}
                        disableFilter
                        disableOuterScrollForList={true}
                        wrapperClassName={styles.displaysWrapper}
                        classNameContent={styles.catalogContent}
                    />
                </div>
                {!isMobile && renderDevicesSidebar()}
            </div>
        )
    }

    const renderAdvertisementDevices = (): React.ReactElement => {
        return (
            <div className={styles.devicesPageWrapper}>
                <div className={styles.catalogWrapper}>
                    <Catalog
                        key={'displays__catalog'}
                        type={'displays'}
                        selectedInfo={selectedInfo}
                        onSelectInfo={setSelectedInfo}
                        selectedItems={selectedItems}
                        onSelect={(items: ItemData[]) => setSelectedItems(items as IExternalAdvertisementDevice[])}
                        onDoubleClickObject={() => {}}
                        onChange={() => {}}
                        useWindow={false}
                        disableFilter
                        disableOuterScrollForList={true}
                        classNameContent={styles.catalogContent}
                        onlyWithDoohDspSettings={true}
                    />
                </div>
                {!isMobile && renderDevicesSidebar()}
            </div>
        )
    }

    const handleTabChange = (tabId: string): void => {
        if (isExternalAdvertisementTabId(tabId)) {
            dispatch(appActions.toggleMobToolbar(false))
            setSelectedTab(tabId)
        }
    }

    return (
        <Content
            title={translate('externalAdvertisement')}
            fullHeight
            toolbar={isMobile && hasDevicesSidebar ? renderDevicesSidebar() : undefined}
            toolbarClassName={styles.mobileToolbar}
        >
            <Typography type="title" className={styles.title}>
                {translate('externalAdvertisement')}
            </Typography>
            <div>
                <TabList
                    tabs={tabList}
                    activeTab={selectedTab}
                    ariaLabel={translate('eventJournal')}
                    onChange={handleTabChange}
                />
            </div>
            <div className={styles.content}>
                {selectedTab === 'devices' && renderDevices()}
                {selectedTab === 'advertisementDevices' && renderAdvertisementDevices()}
                {selectedTab === 'contentModeration' && <ContentModeration onPendingCountChange={setModerationCount} />}
            </div>
        </Content>
    )
}
